import { useTranslation } from 'react-i18next';
import { useEffectOnChange } from '@/shared/hooks';
import { useCallback, useRef, useState } from 'react';
import { OrderPointDto } from '@/app/store/api/restApi';
import { InputedCityInfoType } from '../OrderPointDelivery';
import { useGetOrderPointByCityMutation, ymapsTypes } from '@/entities';

interface UsePointOrderDeliveryProps {
  setMapError: (error: string) => void;
  inputedCityInfo: InputedCityInfoType;
  setCityError: (error: string) => void;
  selectedOrderPoint: OrderPointDto | null;
  setPointCoords: (pointCoords: number[]) => void;
  setOrderPointList: (orderPointList: OrderPointDto[]) => void;
  setSelectedPointsArray: (orderPoint: OrderPointDto[]) => void;
  setSelectedOrderPoint: (orderPoint: OrderPointDto | null) => void;
}

export const usePointOrderDelivery = ({
  setMapError,
  setCityError,
  setPointCoords,
  inputedCityInfo,
  setOrderPointList,
  selectedOrderPoint,
  setSelectedOrderPoint,
  setSelectedPointsArray,
}: UsePointOrderDeliveryProps) => {
  const { t: tAddress } = useTranslation('address');
  const ymaps = useRef<typeof ymapsTypes.Map | undefined>(undefined);
  const mapRef = useRef<typeof ymapsTypes.Map | undefined>(undefined);

  const [isLoading, setIsLoading] = useState(false);

  const [getOrderPointByCity] = useGetOrderPointByCityMutation();

  const onLoadMap = (ympasInstance: typeof ymapsTypes) => {
    ymaps.current = ympasInstance;

    if (!ympasInstance) {
      setMapError(tAddress('map.mapLoadError'));
    }

    if (!ymaps.current) return;
  };

  const fetchOrderPoints = useCallback(
    async (orderPointId?: string) => {
      const areaCoords = inputedCityInfo.area;

      if (!areaCoords) return;
      try {
        setIsLoading(true);
        const orderPoint = await getOrderPointByCity({ findOrderPointsDto: { area: areaCoords } }).unwrap();
        setOrderPointList(orderPoint.data ?? []);
        setSelectedOrderPoint(null);
        setSelectedPointsArray([]);

        if (orderPointId) {
          const selectedOP = orderPoint.data?.find((o) => o.id === orderPointId);
          if (selectedOP) {
            setSelectedOrderPoint(selectedOP);
            const pointsArray = orderPoint.data?.filter(
              (o) => o.location.address.full === selectedOP.location.address.full,
            );
            setSelectedPointsArray(pointsArray ?? []);
          }
        }
      } catch {
        setCityError(tAddress('map.orderPointNotFound'));
        setOrderPointList([]);
        setSelectedOrderPoint(null);
        setSelectedPointsArray([]);
      } finally {
        setIsLoading(false);
      }
    },
    [
      tAddress,
      setCityError,
      inputedCityInfo,
      setOrderPointList,
      getOrderPointByCity,
      setSelectedOrderPoint,
      setSelectedPointsArray,
    ],
  );

  useEffectOnChange(() => {
    if (selectedOrderPoint) {
      const lat = selectedOrderPoint.location.latitude;
      const lng = selectedOrderPoint.location.longitude;
      setPointCoords([lat, lng]);
    } else if (inputedCityInfo.coordsCity) {
      setPointCoords([inputedCityInfo.coordsCity.lat, inputedCityInfo.coordsCity.lng]);
    }
  }, [inputedCityInfo.coordsCity, selectedOrderPoint]);

  return { onLoadMap, mapRef, fetchOrderPoints, isLoading };
};
